import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Uuid } from '@/api/types';
import type { OrganType } from '@/api/apis/case';

export interface CaseTabType {
  id: Uuid;
  donorId: string;
  caseSuffixes: string;
  organType: OrganType;
  lastVisitedPath?: string;
}

export interface CaseTabsState {
  tabsList: CaseTabType[];
  addTab: (data: CaseTabType) => void;
  removeTab: (id: string) => void;
  patchTab: (id: Uuid, patch: Partial<CaseTabType>) => void;
  reorderTab: (draggedId: string, targetId: string) => void;
}

export interface UseCaseTabsStore {
  <T>(selector: (state: CaseTabsState) => T): T;
}

export interface UseCaseTabsConfig<TCaseInfo = unknown> {
  store: UseCaseTabsStore;
  onBeforeNavigate?: (caseId: string, caseInfo?: TCaseInfo) => void;
  homeRoute?: string;
}

export const useCaseTabs = <TCaseInfo = unknown>({
  store,
  onBeforeNavigate,
  homeRoute = '/',
}: UseCaseTabsConfig<TCaseInfo>) => {
  const tabsList = store((s) => s.tabsList);
  const addTab = store((s) => s.addTab);
  const removeTab = store((s) => s.removeTab);
  const patchTab = store((s) => s.patchTab);
  const reorderTab = store((s) => s.reorderTab);

  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();

  const checkCurrentCase = useCallback((id: string) => caseId === id, [caseId]);

  const openCaseDetails = useCallback(
    (targetCaseId: string, caseInfo?: TCaseInfo) => {
      onBeforeNavigate?.(targetCaseId, caseInfo);

      const existingTab = tabsList.find((tab) => tab.id === targetCaseId);
      const targetPath = existingTab?.lastVisitedPath ?? `/case/${targetCaseId}/details`;

      navigate(targetPath);
    },
    [navigate, onBeforeNavigate, tabsList],
  );

  const onTabRemove = useCallback(
    (id: string) => {
      removeTab(id);

      if (caseId === id) {
        navigate(homeRoute);
      }
    },
    [removeTab, navigate, caseId, homeRoute],
  );

  const onCasePathVisited = useCallback(
    (visitedCaseId: string, path: string) => {
      patchTab(visitedCaseId as Uuid, { lastVisitedPath: path });
    },
    [patchTab],
  );

  return {
    tabsList,
    checkCurrentCase,
    addTab,
    patchTab,
    reorderTab,
    openCaseDetails,
    onTabRemove,
    onCasePathVisited,
  };
};
